import { useAsync } from 'react-use';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';

/**
 * Interface para informações de versão retornadas pela API
 */
export interface VersionInfo {
  version: string;
  title: string;
  aliases: string[];
}

/**
 * Hook para buscar versões disponíveis da API backend
 */
export const useVersions = () => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const { entityRef } = useTechDocsReaderPage();

  return useAsync(async () => {
    if (!entityRef) {
      return null;
    }

    // entityRef é do tipo CompoundEntityRef { kind, namespace, name }
    const { kind, namespace, name } = entityRef;

    try {
      // Obter a URL base do backend a partir da configuração
      const backendUrl = configApi.getString('backend.baseUrl');
      const versionsUrl = `${backendUrl}/api/techdocs-versions/versions/${namespace}/${kind.toLowerCase()}/${name}`;

      const response = await fetchApi.fetch(versionsUrl);

      if (!response.ok) {
        // Se for 404, arquivo versions.json não existe, retorna null para não renderizar o componente
        if (response.status === 404) {
          return null;
        }
        // Para outros erros HTTP (500, 401, etc.), fazer throw para propagação adequada
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const versions: VersionInfo[] = await response.json();
      return versions;
    } catch (error) {
      // Para erros de rede, parsing JSON, etc., fazer rethrow para propagação adequada
      throw error;
    }
  }, [entityRef?.kind, entityRef?.namespace, entityRef?.name, fetchApi, configApi]);
};
